import agentTourPlaceholder from "../assets/agent-tour-placeholder.jpg.asset.json";
import corePlaceholder from "../assets/core-placeholder.jpg.asset.json";
import corePlusPlaceholder from "../assets/core-plus-placeholder.jpg.asset.json";
import essentialsPlaceholder from "../assets/essentials-placeholder.jpg.asset.json";
import propertyTourPlaceholder from "../assets/property-tour-placeholder.jpg.asset.json";
import propertyTourImage from "../assets/property-tour.jpg.asset.json";
import showcasePlaceholder from "../assets/showcase-placeholder.jpg.asset.json";
import strEssentialsPlaceholder from "../assets/str-essentials-placeholder.jpg.asset.json";
import strFullPlaceholder from "../assets/str-full-placeholder.jpg.asset.json";
import strLifestylePlaceholder from "../assets/str-lifestyle-placeholder.jpg.asset.json";
import signaturePlaceholder from "../assets/signature-placeholder.jpg.asset.json";
import tour3dPlaceholder from "../assets/3d-tour-placeholder.png.asset.json";

export type PriceTier = "all" | "rental" | "0-4m" | "4m-plus";

export const priceTierLabels: Record<PriceTier, string> = {
  "all": "All Packages",
  "rental": "Rental",
  "0-4m": "$0 – $4M",
  "4m-plus": "$4M+",
};

export interface Package {
  id: string;
  name: string;
  startingAt: string;
  priceNote?: string;
  description: string;
  features: string[];
  popular: boolean;
  badge?: string;
  priceTiers?: PriceTier[];
  category: "residential" | "commercial" | "str";
  crew?: string;
  exampleUrl?: string;
  priceMatrix?: Record<string, number | null>;
  placeholderImage?: string;
  placeholderPosition?: string;
}


// Shared sqft brackets for residential dynamic pricing
export interface SqftBracket {
  id: string;
  label: string;
  short: string;
}

export const sqftBrackets: SqftBracket[] = [
  { id: "0-1999", label: "0 – 1,999 SqFt", short: "0–2k" },
  { id: "2000-2999", label: "2,000 – 2,999 SqFt", short: "2–3k" },
  { id: "3000-3999", label: "3,000 – 3,999 SqFt", short: "3–4k" },
  { id: "4000-4999", label: "4,000 – 4,999 SqFt", short: "4–5k" },
  { id: "5000-5999", label: "5,000 – 5,999 SqFt", short: "5–6k" },
  { id: "6000-6999", label: "6,000 – 6,999 SqFt", short: "6–7k" },
  { id: "7000-7999", label: "7,000 – 7,999 SqFt", short: "7–8k" },
  { id: "8000-8999", label: "8,000 – 8,999 SqFt", short: "8–9k" },
  { id: "9000-9999", label: "9,000 – 9,999 SqFt", short: "9–10k" },
];

export const DEFAULT_BRACKET = "0-1999";

export function formatPackagePrice(pkg: Package, bracketId: string): string {
  if (!pkg.priceMatrix) return pkg.startingAt;
  const price = pkg.priceMatrix[bracketId];
  if (price == null) return "Custom quote";
  return `$${price.toLocaleString("en-US")}`;
}

export function getBracketLabel(bracketId: string): string {
  return sqftBrackets.find((b) => b.id === bracketId)?.label ?? bracketId;
}

// Individual services for single-service requests (video only, matterport only, etc.)
export interface IndividualService {
  id: string;
  name: string;
  description: string;
  startingPrice: string;
  category: "video" | "photo" | "3d";
}

export const individualServices: IndividualService[] = [
  {
    id: "signature-reel",
    name: "Signature Reel",
    description: "Luxury cinematic vertical video with full creative approach",
    startingPrice: "$1,500",
    category: "video",
  },
  {
    id: "property-film",
    name: "Property Film",
    description: "High-end cinematic property film with advanced composition",
    startingPrice: "$1,250",
    category: "video",
  },
  {
    id: "agent-highlight",
    name: "Agent Highlight",
    description: "Budget-friendly on-camera agent video for social media",
    startingPrice: "$700",
    category: "video",
  },
  {
    id: "property-highlight",
    name: "Property Highlight",
    description: "Streamlined cost-effective property video for marketing",
    startingPrice: "$450",
    category: "video",
  },
  {
    id: "premium-listing-photos",
    name: "Premium Listing Photos",
    description: "High-end MLS photography with architectural approach",
    startingPrice: "$350",
    category: "photo",
  },
  {
    id: "listing-photos",
    name: "Listing Photos",
    description: "Professional MLS photography with clean accurate visuals",
    startingPrice: "$199",
    category: "photo",
  },
  {
    id: "twilight-shoot",
    name: "Twilight Shoot",
    description: "Dusk exterior photos with interior lights on",
    startingPrice: "$300",
    category: "photo",
  },
  {
    id: "matterport-3d",
    name: "Matterport 3D",
    description: "Immersive 3D tour with interactive navigation and 2D floor plan",
    startingPrice: "$250",
    category: "3d",
  },
];

export const residentialPackages: Package[] = [
  {
    id: "signature",
    name: "Signature",
    startingAt: "$2,695",
    description: "All-inclusive luxury marketing combining premium photography, cinematic video, and full creative direction.",
    features: [
      "Premium Listing Photos",
      "Twilight/Sunrise Photos",
      "Signature Vertical Reel",
      "Signature Property Film",
      "Drone Photos & Video",
      "2D & 3D Floor Plans",
      "3D Tour",
      "Agent On-Camera",
      "Full Creative Script",
      "Building Amenities",
      "Property Website",
      "Studio-Grade Retouching",
    ],
    popular: false,
    badge: "All-Inclusive",
    priceTiers: ["4m-plus"],
    category: "residential",
    crew: "2–3 Creative Specialists",
    placeholderImage: signaturePlaceholder.url,
    placeholderPosition: "center 45%",
    exampleUrl: "https://tours.photostetic.com/11420nbayshoredr?b=0",
    priceMatrix: {
      "0-1999": 2695, "2000-2999": 2995, "3000-3999": 3295, "4000-4999": 3595,
      "5000-5999": 3895, "6000-6999": 4195, "7000-7999": 4495, "8000-8999": 4795, "9000-9999": 5095,
    },
  },
  {
    id: "showcase",
    name: "Showcase",
    startingAt: "$1,695",
    priceNote: "Starting",
    description: "Premium marketing package with vertical reels, cinematic video, and aerial photography.",
    features: [
      "Premium Listing Photos",
      "Drone Photos & Video",
      "Vertical Reel (45-60s)",
      "Property Film (90s)",
      "2D Floor Plan",
      "3D Tour",
      "Building Amenities",
      "Property Website",
      "Studio-Grade Retouching",
    ],
    popular: false,
    priceTiers: ["4m-plus"],
    category: "residential",
    crew: "2 Creative Specialists",
    exampleUrl: "https://tours.photostetic.com/3tahitibeachislandrd?b=0",
    placeholderImage: showcasePlaceholder.url,
    priceMatrix: {
      "0-1999": 1695, "2000-2999": 1895, "3000-3999": 2095, "4000-4999": 2295,
      "5000-5999": 2495, "6000-6999": 2695, "7000-7999": 2895, "8000-8999": 3095, "9000-9999": 3295,
    },
  },
  {
    id: "agent-tour",
    name: "Agent Tour",
    startingAt: "$949",
    description: "Personalized property tour featuring agent on-camera introduction and property highlights.",
    features: [
      "Wide-Angle Photos",
      "Drone Photos & Video",
      "60s Agent Intro Video",
      "Agent On-Camera",
      "2D Floor Plan",
      "Building Amenities",
      "Property Website",
      "Studio-Grade Retouching",
    ],
    popular: false,
    priceTiers: ["0-4m"],
    category: "residential",
    crew: "1–2 Creative Specialists",
    placeholderImage: agentTourPlaceholder.url,
    placeholderPosition: "center 30%",
    priceMatrix: {
      "0-1999": 949, "2000-2999": 1049, "3000-3999": 1149, "4000-4999": 1249,
      "5000-5999": 1349, "6000-6999": 1449, "7000-7999": 1549, "8000-8999": 1649, "9000-9999": 1749,
    },
  },
  {
    id: "property-tour",
    name: "Property Tour",
    startingAt: "$649",
    description: "Complete visual package with photography, video walkthrough, and all essential marketing assets.",
    features: [
      "Wide-Angle Photos",
      "Drone Photos & Video",
      "60s Highlight Video",
      "2D Floor Plan",
      "Building Amenities",
      "Property Website",
      "Studio-Grade Retouching",
    ],
    popular: false,
    priceTiers: ["0-4m"],
    category: "residential",
    crew: "1 Creative Specialist",
    placeholderImage: propertyTourImage.url,
    priceMatrix: {
      "0-1999": 649, "2000-2999": 749, "3000-3999": 849, "4000-4999": 949,
      "5000-5999": 1049, "6000-6999": 1149, "7000-7999": 1249, "8000-8999": 1349, "9000-9999": 1449,
    },
  },
  {
    id: "property-tour-light",
    name: "Property Tour Light",
    startingAt: "$499",
    priceNote: "Starting",
    description: "Essential photo and video coverage for smaller listings, without aerials or extras.",
    features: [
      "Wide-Angle Photos",
      "60s Highlight Video",
      "Studio-Grade Retouching",
    ],
    popular: false,
    priceTiers: ["0-4m"],
    category: "residential",
    crew: "1 Creative Specialist",
    placeholderImage: propertyTourPlaceholder.url,
    priceMatrix: {
      "0-1999": 499, "2000-2999": 599, "3000-3999": null, "4000-4999": null,
      "5000-5999": null, "6000-6999": null, "7000-7999": null, "8000-8999": null, "9000-9999": null,
    },
  },
  {
    id: "3d-tour",
    name: "3D Tour",
    startingAt: "$449",
    description: "Immersive 3D walkthrough experience with interactive navigation and detailed floor plans.",
    features: [
      "Wide-Angle Photos",
      "Drone Photos",
      "3D Tour (Matterport or Zillow)",
      "2D Floor Plan",
      "Building Amenities",
      "Property Website",
      "Studio-Grade Retouching",
    ],
    popular: false,
    priceTiers: ["0-4m"],
    category: "residential",
    crew: "1 Creative Specialist",
    placeholderImage: tour3dPlaceholder.url,
    priceMatrix: {
      "0-1999": 449, "2000-2999": 549, "3000-3999": 649, "4000-4999": 749,
      "5000-5999": 849, "6000-6999": 949, "7000-7999": 1049, "8000-8999": 1149, "9000-9999": 1249,
    },
  },
  {
    id: "core-plus",
    name: "Core Plus",
    startingAt: "$349",
    description: "Our most popular media combo: wide-angle photos, drone, and 2D floor plan.",
    features: [
      "Wide-Angle Photos",
      "Drone Photos",
      "2D Floor Plan",
      "Building Amenities",
      "Property Website",
      "Studio-Grade Retouching",
      "24H Delivery",
    ],
    popular: true,
    priceTiers: ["rental", "0-4m"],
    category: "residential",
    crew: "1 Creative Specialist",
    placeholderImage: corePlusPlaceholder.url,
    priceMatrix: {
      "0-1999": 349, "2000-2999": 399, "3000-3999": 449, "4000-4999": 499,
      "5000-5999": 549, "6000-6999": 599, "7000-7999": 649, "8000-8999": 699, "9000-9999": 749,
    },
  },
  {
    id: "core",
    name: "Core",
    startingAt: "$299",
    description: "Everything in Essentials, plus drone photos for aerial context and enhanced curb appeal.",
    features: [
      "Wide-Angle Photos",
      "Drone Photos",
      "Building Amenities",
      "Property Website",
      "Studio-Grade Retouching",
      "24H Delivery",
    ],
    popular: false,
    priceTiers: ["rental", "0-4m"],
    category: "residential",
    crew: "1 Creative Specialist",
    placeholderImage: corePlaceholder.url,
    priceMatrix: {
      "0-1999": 299, "2000-2999": 349, "3000-3999": 399, "4000-4999": 449,
      "5000-5999": 499, "6000-6999": 549, "7000-7999": 599, "8000-8999": 649, "9000-9999": 699,
    },
  },
  {
    id: "essentials",
    name: "Essentials",
    startingAt: "$199",
    description: "Entry-level MLS package providing fundamental visuals to list a property clearly and effectively.",
    features: [
      "Listing Photos",
      "Standard Retouching",
      "24H Delivery",
    ],
    popular: false,
    priceTiers: ["rental"],
    category: "residential",
    crew: "1 Creative Specialist",
    placeholderImage: essentialsPlaceholder.url,
    priceMatrix: {
      "0-1999": 199, "2000-2999": 249, "3000-3999": 299, "4000-4999": 349,
      "5000-5999": 399, "6000-6999": 449, "7000-7999": 499, "8000-8999": 549, "9000-9999": 599,
    },
  },
];

export const commercialPackages: Package[] = [
  {
    id: "cre-premium",
    name: "CRE Premium Kit",
    startingAt: "$700",
    description: "Premium commercial package featuring highlight video and comprehensive visual marketing.",
    features: [
      "Wide-Angle Photos",
      "Drone Photos",
      "2D Floor Plan",
      "60s Highlight Video",
      "Property Website",
      "Signature Image Enhancement",
      "Priority Delivery",
    ],
    popular: false,
    category: "commercial",
  },
  {
    id: "cre-marketing",
    name: "CRE Marketing Kit",
    startingAt: "$400",
    description: "Complete marketing package with floor plans and enhanced visual assets for commercial properties.",
    features: [
      "Wide-Angle Photos",
      "Drone Photos",
      "2D Floor Plan",
      "Property Website",
      "Signature Image Enhancement",
      "24H Delivery",
    ],
    popular: true,
    category: "commercial",
  },
  {
    id: "cre-essentials",
    name: "CRE Essentials",
    startingAt: "$300",
    description: "Essential visuals for commercial listings with professional photography and aerial coverage.",
    features: [
      "Wide-Angle Photos",
      "Drone Photos",
      "Property Website",
      "Signature Image Enhancement",
      "24H Delivery",
    ],
    popular: false,
    category: "commercial",
  },
];

export const strPackages: Package[] = [
  {
    id: "str-full",
    name: "STR Full Coverage",
    startingAt: "$495",
    description: "Complete visual package including aerial photography and floor plans for maximum booking appeal.",
    features: [
      "Wide-Angle Photos",
      "Lifestyle & Detail Shots",
      "Inventory Shots",
      "Building Amenities",
      "Drone Photos",
      "Signature Image Enhancement",
      "2D Floor Plan",
    ],
    popular: false,
    category: "str",
    crew: "1–2 Creative Specialists",
    placeholderImage: strFullPlaceholder.url,
    priceMatrix: {
      "0-3000": 495, "3001-4000": 595, "4001-5000": 695, "5001-6000": 795,
      "6001-7000": 895, "7001-8000": 995, "8001-9000": 1095, "9001-10000": 1195,
    },
  },
  {
    id: "str-lifestyle",
    name: "STR Lifestyle & Inventory",
    startingAt: "$395",
    description: "Comprehensive package with lifestyle shots and detailed inventory photography for premium listings.",
    features: [
      "Wide-Angle Photos",
      "Lifestyle & Detail Shots",
      "Inventory Shots",
      "Building Amenities",
      "Signature Image Enhancement",
      "Platform Optimized",
    ],
    popular: true,
    category: "str",
    crew: "1 Creative Specialist",
    placeholderImage: strLifestylePlaceholder.url,
    priceMatrix: {
      "0-3000": 395, "3001-4000": 450, "4001-5000": 500, "5001-6000": 550,
      "6001-7000": 600, "7001-8000": 650, "8001-9000": 700, "9001-10000": 750,
    },
  },
  {
    id: "str-essentials",
    name: "STR Essentials",
    startingAt: "$250",
    description: "Essential photography package for vacation rentals with signature image enhancement.",
    features: [
      "Wide-Angle Photos",
      "Signature Image Enhancement",
      "Airbnb/VRBO Optimized",
      "One-Day Turnaround",
    ],
    popular: false,
    category: "str",
    crew: "1 Creative Specialist",
    placeholderImage: strEssentialsPlaceholder.url,
    priceMatrix: {
      "0-3000": 250, "3001-4000": 300, "4001-5000": 350, "5001-6000": 400,
      "6001-7000": 450, "7001-8000": 500, "8001-9000": 550, "9001-10000": 600,
    },
  },
];

// All packages combined for easy lookup
export const allPackages: Package[] = [
  ...residentialPackages,
  ...commercialPackages,
  ...strPackages,
];

// Human-readable service summary for each package (used in chatbot selectors)
export const packageSummaryLabels: Record<string, string> = {
  essentials: "Interior + Exterior Photos",
  core: "Photos + Drone + Amenities",
  "core-plus": "Photos + Drone + Floor Plan + Amenities",
  "3d-tour": "Photos + 3D Tour + Drone + Floor Plan",
  "property-tour": "Photos + Video + Drone + Floor Plan",
  "property-tour-light": "Photos + Video",
  "agent-tour": "Photos + Agent Video + Drone + Floor Plan",
  showcase: "Photos + Twilight + Video + Reel + 3D Tour",
  signature: "Photos + Twilight + Film + Reel + 3D + Agent",
  "cre-essentials": "Photos + Drone",
  "cre-marketing": "Photos + Drone + Floor Plan",
  "cre-premium": "Photos + Drone + Video + Floor Plan",
  "str-essentials": "Essential STR photos",
  "str-lifestyle": "Lifestyle & detail shots",
  "str-full": "Full STR marketing package",
};

// Helper to find package by ID
export const getPackageById = (id: string): Package | undefined => 
  allPackages.find(pkg => pkg.id === id);

// Project types for the chatbot (simplified)
export const projectTypes = [
  "MLS For Sale",
  "MLS For Rent",
  "Short-term Rental",
  "Something Else",
] as const;

export type ProjectType = typeof projectTypes[number];
